import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, RefreshCw, CheckCircle2, Circle, FlaskConical } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { ErrorState } from "@/components/empty-state";

export const Route = createFileRoute("/admin/gamivo")({
  head: () => ({ meta: [
    { title: "GAMIVO Imports — NexKey Admin" }, { name: "description", content: "Manage controlled GAMIVO catalog imports." },
    { property: "og:title", content: "GAMIVO Imports — NexKey Admin" }, { property: "og:description", content: "NexKey GAMIVO import management." },
    { property: "og:type", content: "website" }, { name: "twitter:card", content: "summary" }, { name: "robots", content: "noindex,nofollow" },
  ] }),
  component: AdminGamivo,
});

function AdminGamivo() {
  const qc = useQueryClient();
  const [limit, setLimit] = useState<string>("");
  const [mode, setMode] = useState<"game" | "gift_card">("game");
  const status = useQuery({
    queryKey: ["admin-gamivo-status"],
    queryFn: () => api.adminGamivoStatus(),
    refetchInterval: 4000,
  });
  const errors = useQuery({
    queryKey: ["admin-gamivo-errors"],
    queryFn: () => api.adminImportErrors(),
    refetchInterval: 8000,
  });
  const run = useMutation({
    mutationFn: (opts: { dryRun?: boolean }) =>
      api.adminImportGamivo({
        dryRun: opts.dryRun,
        mode,
        limit: limit ? Number(limit) : mode === "gift_card" ? 25 : undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-gamivo-status"] }),
  });

  const data = status.data?.data;
  const jobs = data?.jobs ?? [];

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">GAMIVO import</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {mode === "gift_card" ? "Gift Cards · controlled test · 25 by default, maximum 100" : "Games"}
            {mode === "game" && <> Downloads the GAMIVO EUR XML feed and syncs offers against existing Steam games.
            Matches use Steam AppID → exact title → base title with edition tag. Unmatched products
            are logged for later review — the importer never auto-creates new games.</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Import catalog" value={mode} onChange={(e) => setMode(e.target.value === "gift_card" ? "gift_card" : "game")} className="rounded-md border border-border bg-surface px-3 py-2 text-sm">
            <option value="game">Games</option><option value="gift_card">Gift Cards · test (max 100)</option>
          </select>
          <input
            type="number"
            min={1}
            max={mode === "gift_card" ? 100 : undefined}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="Limit (optional)"
            className="w-40 rounded-md border border-border bg-surface px-3 py-2 text-sm"
          />
          <button
            onClick={() => run.mutate({ dryRun: true })}
            disabled={run.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <FlaskConical className="h-4 w-4" /> Dry run
          </button>
          <button
            onClick={() => run.mutate({})}
            disabled={run.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            <Play className="h-4 w-4" /> {run.isPending ? "Starting…" : "Run import"}
          </button>
        </div>
      </header>

      {run.isError && <ErrorState message={(run.error as Error).message} />}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Feed</h3>
          <p className="mt-1 break-all text-xs text-muted-foreground">{data?.feedUrl ?? "—"}</p>
          <div className="mt-3 flex items-center gap-1 text-xs">
            {data?.affiliateTagConfigured ? (
              <span className="inline-flex items-center gap-1 text-success">
                <CheckCircle2 className="h-3 w-3" /> affiliate tag configured
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Circle className="h-3 w-3" /> using default "nexkey"
              </span>
            )}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Shop</h3>
          <div className="mt-1 text-sm">{data?.shop?.name ?? "Not created yet — will be created on first run"}</div>
          <div className="text-xs text-muted-foreground">{data?.offerCount ?? 0} active offers</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Last run</h3>
          {jobs[0] ? (
            <div className="mt-1 space-y-1 text-xs">
              <div>{new Date(jobs[0].startedAt).toLocaleString()}</div>
              <div>Status: <span className={
                jobs[0].status === "completed" ? "text-success" :
                jobs[0].status === "running" ? "text-primary" :
                jobs[0].status === "failed" ? "text-destructive" : "text-muted-foreground"
              }>{jobs[0].status}</span></div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1">
                <span className="text-muted-foreground">Matched</span>
                <span className="text-right font-mono">{(jobs[0].matchedItems ?? 0).toLocaleString()}</span>
                <span className="text-muted-foreground">Unmatched</span>
                <span className="text-right font-mono">{(jobs[0].unmatchedItems ?? 0).toLocaleString()}</span>
                <span className="text-muted-foreground">Failed</span>
                <span className="text-right font-mono text-destructive">{jobs[0].failedItems.toLocaleString()}</span>
                <span className="text-muted-foreground">Imported</span>
                <span className="text-right font-mono text-success">{jobs[0].importedItems.toLocaleString()}</span>
                <span className="text-muted-foreground">Total</span>
                <span className="text-right font-mono">{jobs[0].totalItems.toLocaleString()}</span>
              </div>
              {jobs[0].errorMessage && (
                <p className="rounded bg-muted p-2 text-[11px] text-muted-foreground">{jobs[0].errorMessage}</p>
              )}
            </div>
          ) : (
            <div className="mt-1 text-xs text-muted-foreground">No runs yet</div>
          )}
          <button
            onClick={() => status.refetch()}
            className="mt-3 inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-semibold"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent jobs</h2>
        {status.isError && <ErrorState message={(status.error as Error).message} />}
        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No GAMIVO imports have run yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Finished</th>
                  <th className="px-4 py-3 text-right">Matched</th>
                  <th className="px-4 py-3 text-right">Unmatched</th>
                  <th className="px-4 py-3 text-right">Failed</th>
                  <th className="px-4 py-3 text-right">Imported</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-t border-border/60">
                    <td className="px-4 py-2">{j.source}</td>
                    <td className="px-4 py-2">
                      <span className={
                        j.status === "completed" ? "text-success" :
                        j.status === "running" ? "text-primary" :
                        j.status === "failed" ? "text-destructive" : "text-muted-foreground"
                      }>{j.status}</span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{new Date(j.startedAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-muted-foreground">{j.finishedAt ? new Date(j.finishedAt).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2 text-right font-mono">{(j.matchedItems ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono">{(j.unmatchedItems ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono text-destructive">{j.failedItems.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono text-success">{j.importedItems.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono">{j.totalItems.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent unmatched / failed products</h2>
        {errors.isSuccess && errors.data.data.length === 0 && (
          <p className="text-sm text-muted-foreground">No errors logged.</p>
        )}
        {errors.isSuccess && errors.data.data.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {errors.data.data.slice(0, 50).map((e: { id: string; createdAt: string; rawPayload?: { title?: string; name?: string }; errorMessage?: string }) => (
                  <tr key={e.id} className="border-t border-border/60">
                    <td className="px-4 py-2 text-muted-foreground">{new Date(e.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-muted-foreground">{e.rawPayload?.title ?? e.rawPayload?.name ?? "—"}</td>
                    <td className="px-4 py-2 text-destructive">{e.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
